/**
 * Currency → locale mapping and lookup.
 *
 * Lives in `@splitfiesta/shared` rather than `@splitfiesta/i18n` so that
 * packages/core (consumed by apps/api, whose `moduleResolution: node`
 * cannot read package `exports` subpaths) can reach it. `@splitfiesta/i18n`
 * re-exports both symbols, so its public API is unchanged.
 */

// Comprehensive currency-to-locale mapping
// Maps each currency to its primary locale for proper formatting (separators, symbol placement)
export const CURRENCY_LOCALES: Record<string, string> = {
  // Americas
  USD: 'en-US', // US Dollar
  CAD: 'en-CA', // Canadian Dollar
  MXN: 'es-MX', // Mexican Peso
  BRL: 'pt-BR', // Brazilian Real
  ARS: 'es-AR', // Argentine Peso
  CLP: 'es-CL', // Chilean Peso
  COP: 'es-CO', // Colombian Peso
  PEN: 'es-PE', // Peruvian Sol
  UYU: 'es-UY', // Uruguayan Peso
  PYG: 'es-PY', // Paraguayan Guarani
  BOB: 'es-BO', // Bolivian Boliviano
  VES: 'es-VE', // Venezuelan Bolívar
  GTQ: 'es-GT', // Guatemalan Quetzal
  HNL: 'es-HN', // Honduran Lempira
  NIO: 'es-NI', // Nicaraguan Córdoba
  CRC: 'es-CR', // Costa Rican Colón
  PAB: 'es-PA', // Panamanian Balboa
  DOP: 'es-DO', // Dominican Peso
  CUP: 'es-CU', // Cuban Peso

  // Europe
  EUR: 'de-DE', // Euro (German format: 1.234,56 €)
  GBP: 'en-GB', // British Pound
  CHF: 'de-CH', // Swiss Franc
  SEK: 'sv-SE', // Swedish Krona
  NOK: 'nb-NO', // Norwegian Krone
  DKK: 'da-DK', // Danish Krone
  PLN: 'pl-PL', // Polish Zloty
  CZK: 'cs-CZ', // Czech Koruna
  HUF: 'hu-HU', // Hungarian Forint
  RON: 'ro-RO', // Romanian Leu
  BGN: 'bg-BG', // Bulgarian Lev
  HRK: 'hr-HR', // Croatian Kuna
  RSD: 'sr-RS', // Serbian Dinar
  BAM: 'bs-BA', // Bosnia-Herzegovina Convertible Mark
  MKD: 'mk-MK', // Macedonian Denar
  ALL: 'sq-AL', // Albanian Lek
  RUB: 'ru-RU', // Russian Ruble
  UAH: 'uk-UA', // Ukrainian Hryvnia
  BYN: 'be-BY', // Belarusian Ruble

  // Asia-Pacific
  JPY: 'ja-JP', // Japanese Yen
  CNY: 'zh-CN', // Chinese Yuan
  KRW: 'ko-KR', // South Korean Won
  INR: 'en-IN', // Indian Rupee
  SGD: 'en-SG', // Singapore Dollar
  HKD: 'en-HK', // Hong Kong Dollar
  TWD: 'zh-TW', // New Taiwan Dollar
  THB: 'th-TH', // Thai Baht
  MYR: 'ms-MY', // Malaysian Ringgit
  IDR: 'id-ID', // Indonesian Rupiah
  PHP: 'en-PH', // Philippine Peso
  VND: 'vi-VN', // Vietnamese Dong

  // Oceania
  AUD: 'en-AU', // Australian Dollar
  NZD: 'en-NZ', // New Zealand Dollar
  FJD: 'en-FJ', // Fijian Dollar
  PGK: 'en-PG', // Papua New Guinean Kina

  // Middle East
  AED: 'ar-AE', // UAE Dirham
  SAR: 'ar-SA', // Saudi Riyal
  QAR: 'ar-QA', // Qatari Riyal
  BHD: 'ar-BH', // Bahraini Dinar
  KWD: 'ar-KW', // Kuwaiti Dinar
  OMR: 'ar-OM', // Omani Rial
  JOD: 'ar-JO', // Jordanian Dinar
  ILS: 'he-IL', // Israeli New Shekel
  TRY: 'tr-TR', // Turkish Lira

  // Africa
  ZAR: 'en-ZA', // South African Rand
  EGP: 'ar-EG', // Egyptian Pound
  MAD: 'ar-MA', // Moroccan Dirham
  TND: 'ar-TN', // Tunisian Dinar
  DZD: 'ar-DZ', // Algerian Dinar
  NGN: 'en-NG', // Nigerian Naira
  KES: 'en-KE', // Kenyan Shilling
  GHS: 'en-GH', // Ghanaian Cedi
  ETB: 'am-ET', // Ethiopian Birr
  TZS: 'sw-TZ', // Tanzanian Shilling
  UGX: 'en-UG', // Ugandan Shilling

  // Other regions
  ISK: 'is-IS', // Icelandic Króna
  PKR: 'ur-PK', // Pakistani Rupee
  LKR: 'si-LK', // Sri Lankan Rupee
  BDT: 'bn-BD', // Bangladeshi Taka
  NPR: 'ne-NP', // Nepalese Rupee
  AFN: 'ps-AF', // Afghan Afghani
  IRR: 'fa-IR', // Iranian Rial
  IQD: 'ar-IQ', // Iraqi Dinar
  LYD: 'ar-LY', // Libyan Dinar
  SYP: 'ar-SY', // Syrian Pound
  LBP: 'ar-LB', // Lebanese Pound

  // Currencies without specific locale use en-US as default
  // This includes many smaller currencies and those used in multiple regions
};

/**
 * Get the primary locale for a currency
 * @param currencyCode The ISO currency code
 * @returns The primary locale where this currency is used
 */
export function getCurrencyLocale(currencyCode: string): string {
  return CURRENCY_LOCALES[currencyCode] || 'en-US';
}
